import React, { useEffect, useState } from "react";
import api, { formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, Empty } from "@/components/app/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, KeyRound, Copy, Trash2, Ban, Webhook, Send, RefreshCw, Code2 } from "lucide-react";

/* API access — keys for partners to read from SAHOD, webhooks for SAHOD to
 * tell partners when things happen. */

export default function ApiAccess() {
    const [keys, setKeys] = useState([]);
    const [scopes, setScopes] = useState([]);
    const [usage, setUsage] = useState(null);
    const [hooks, setHooks] = useState([]);
    const [events, setEvents] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [newKey, setNewKey] = useState(null);
    const [newHook, setNewHook] = useState(null);
    const [keyForm, setKeyForm] = useState({ name: "", scopes: [], rate_limit_per_hour: 600, expires_in_days: "" });
    const [hookForm, setHookForm] = useState({ url: "", events: [], description: "" });
    const [openKey, setOpenKey] = useState(false);
    const [openHook, setOpenHook] = useState(false);

    const load = () => Promise.all([
        api.get("/api-admin/keys").then((r) => setKeys(r.data.items || [])),
        api.get("/api-admin/scopes").then((r) => setScopes(r.data.items || [])),
        api.get("/api-admin/usage").then((r) => setUsage(r.data)),
        api.get("/api-admin/webhooks").then((r) => setHooks(r.data.items || [])),
        api.get("/api-admin/webhook-events").then((r) => setEvents(r.data.items || [])),
        api.get("/api-admin/webhooks/deliveries").then((r) => setDeliveries(r.data.items || [])),
    ]).catch(() => {});
    useEffect(() => { load(); }, []);

    const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"), () => {});

    const createKey = async () => {
        try {
            const payload = { ...keyForm };
            if (!payload.expires_in_days) delete payload.expires_in_days;
            const { data } = await api.post("/api-admin/keys", payload);
            setNewKey(data); setOpenKey(false);
            setKeyForm({ name: "", scopes: [], rate_limit_per_hour: 600, expires_in_days: "" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not create the key"); }
    };

    const revokeKey = async (k) => {
        if (!window.confirm(`Revoke "${k.name}"? Any integration using it stops working immediately.`)) return;
        try { await api.post(`/api-admin/keys/${k.id}/revoke`); toast.success("Key revoked"); load(); }
        catch { toast.error("Could not revoke it"); }
    };

    const deleteKey = async (k) => {
        if (!window.confirm(`Delete "${k.name}" permanently?`)) return;
        try { await api.delete(`/api-admin/keys/${k.id}`); toast.success("Key deleted"); load(); }
        catch { toast.error("Could not delete it"); }
    };

    const createHook = async () => {
        try {
            const { data } = await api.post("/api-admin/webhooks", hookForm);
            setNewHook(data); setOpenHook(false); setHookForm({ url: "", events: [], description: "" });
            load();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not create the endpoint"); }
    };

    const testHook = async (h) => {
        try {
            const { data } = await api.post(`/api-admin/webhooks/${h.id}/test`);
            data.ok ? toast.success(`Receiver answered ${data.response_status}`)
                    : toast.error(`Delivery failed: ${data.response_body || data.response_status}`);
            load();
        } catch { toast.error("Could not send the test"); }
    };

    const toggleHook = async (h) => {
        try { await api.patch(`/api-admin/webhooks/${h.id}`, { active: !h.active }); load(); }
        catch { toast.error("Could not update it"); }
    };

    const deleteHook = async (h) => {
        if (!window.confirm(`Remove ${h.url}?`)) return;
        try { await api.delete(`/api-admin/webhooks/${h.id}`); toast.success("Endpoint removed"); load(); }
        catch { toast.error("Could not remove it"); }
    };

    const rotateHookSecret = async (h) => {
        if (!window.confirm("Issue a new signing secret? Deliveries will fail until the receiver is updated.")) return;
        try { const { data } = await api.post(`/api-admin/webhooks/${h.id}/rotate-secret`); setNewHook({ ...h, secret: data.secret }); }
        catch { toast.error("Could not rotate the secret"); }
    };

    const toggle = (list, value, set) =>
        set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

    const origin = typeof window !== "undefined" ? window.location.origin : "";

    return (
        <div>
            <PageHeader title="API Access"
                subtitle="Issue scoped keys so other systems can read from SAHOD, and register webhooks so SAHOD can tell them when things change." />

            <Tabs defaultValue="keys">
                <TabsList>
                    <TabsTrigger value="keys">API Keys</TabsTrigger>
                    <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
                    <TabsTrigger value="docs">Getting started</TabsTrigger>
                </TabsList>

                {/* ---------------- keys ---------------- */}
                <TabsContent value="keys">
                    <div className="flex justify-end my-3">
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                                onClick={() => setOpenKey(true)} data-testid="api-new-key">
                            <Plus className="size-4" /> New API key
                        </button>
                    </div>

                    {!keys.length && <Surface className="p-8"><Empty title="No API keys" subtitle="Issue one to let an outside system read from SAHOD." /></Surface>}

                    <div className="grid gap-3 md:grid-cols-2">
                        {keys.map((k) => (
                            <Surface key={k.id} className="p-4" data-testid={`api-key-${k.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <KeyRound className="size-4 text-primary shrink-0" />
                                            <span className="font-semibold truncate">{k.name}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${k.active
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : "bg-red-50 text-red-700 border-red-200"}`}>
                                                {k.active ? "Active" : "Revoked"}
                                            </span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1.5 space-y-0.5">
                                            <div className="font-mono">…{k.key_hint} · {k.rate_limit_per_hour}/hr · {k.call_count || 0} calls</div>
                                            <div>{k.last_used_at ? `Last used ${formatDateTime(k.last_used_at)}` : "Never used"}</div>
                                            {k.expires_at && <div>Expires {formatDateTime(k.expires_at)}</div>}
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {(k.scopes || []).map((s) => (
                                                <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary">{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1 shrink-0">
                                        {k.active && (
                                            <button className="text-xs px-2 py-1 rounded border border-border flex items-center gap-1" onClick={() => revokeKey(k)}>
                                                <Ban className="size-3" /> Revoke
                                            </button>
                                        )}
                                        <button className="text-xs px-2 py-1 rounded border border-destructive/40 text-destructive flex items-center gap-1" onClick={() => deleteKey(k)}>
                                            <Trash2 className="size-3" /> Delete
                                        </button>
                                    </div>
                                </div>
                            </Surface>
                        ))}
                    </div>

                    {usage?.keys?.length > 0 && (
                        <Surface className="p-4 mt-4">
                            <h3 className="text-sm font-semibold mb-2">Usage — last 24 hours</h3>
                            <div className="space-y-1 text-sm">
                                {usage.keys.map((u) => (
                                    <div key={u.key_id} className="flex items-center justify-between border-b border-border last:border-0 py-1.5">
                                        <span>{u.key_name}</span>
                                        <span className="font-mono text-xs">
                                            {u.calls} calls
                                            {u.errors > 0 && <span className="text-red-600 ml-2">{u.errors} errors</span>}
                                            <span className="text-muted-foreground ml-2">{u.avg_ms}ms avg</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Surface>
                    )}
                </TabsContent>

                {/* ---------------- webhooks ---------------- */}
                <TabsContent value="webhooks">
                    <div className="flex justify-end my-3">
                        <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2"
                                onClick={() => setOpenHook(true)} data-testid="api-new-hook">
                            <Plus className="size-4" /> New webhook
                        </button>
                    </div>

                    {!hooks.length && <Surface className="p-8"><Empty title="No webhooks" subtitle="Register a URL and SAHOD will POST to it when the events you pick happen." /></Surface>}

                    <div className="space-y-3">
                        {hooks.map((h) => (
                            <Surface key={h.id} className="p-4" data-testid={`api-hook-${h.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <Webhook className="size-4 text-primary shrink-0" />
                                            <span className="font-mono text-sm truncate">{h.url}</span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${h.active
                                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : "bg-muted text-muted-foreground border-border"}`}>
                                                {h.active ? "Active" : "Paused"}
                                            </span>
                                            {h.consecutive_failures > 0 && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                                                    {h.consecutive_failures} failures in a row
                                                </span>
                                            )}
                                        </div>
                                        {h.description && <div className="text-xs text-muted-foreground mt-1">{h.description}</div>}
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {(h.events || []).map((e) => (
                                                <span key={e} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary">{e}</span>
                                            ))}
                                        </div>
                                        {h.disabled_reason && <div className="text-xs text-red-700 mt-1.5">Disabled: {h.disabled_reason}</div>}
                                    </div>
                                    <div className="flex flex-col gap-1 shrink-0">
                                        <button className="text-xs px-2 py-1 rounded border border-border flex items-center gap-1" onClick={() => testHook(h)}>
                                            <Send className="size-3" /> Test
                                        </button>
                                        <button className="text-xs px-2 py-1 rounded border border-border" onClick={() => toggleHook(h)}>
                                            {h.active ? "Pause" : "Resume"}
                                        </button>
                                        <button className="text-xs px-2 py-1 rounded border border-border flex items-center gap-1" onClick={() => rotateHookSecret(h)}>
                                            <RefreshCw className="size-3" /> New secret
                                        </button>
                                        <button className="text-xs px-2 py-1 rounded border border-destructive/40 text-destructive flex items-center gap-1" onClick={() => deleteHook(h)}>
                                            <Trash2 className="size-3" /> Remove
                                        </button>
                                    </div>
                                </div>
                            </Surface>
                        ))}
                    </div>

                    {deliveries.length > 0 && (
                        <Surface className="p-0 mt-4 overflow-hidden">
                            <h3 className="text-sm font-semibold px-4 pt-4 pb-2">Recent deliveries</h3>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40">
                                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                            <th className="px-3 py-2">When</th><th className="px-3 py-2">Event</th>
                                            <th className="px-3 py-2">Status</th><th className="px-3 py-2">HTTP</th>
                                            <th className="px-3 py-2">Attempt</th><th className="px-3 py-2">Response</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deliveries.slice(0, 25).map((d) => (
                                            <tr key={d.id} className="border-t border-border">
                                                <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(d.timestamp)}</td>
                                                <td className="px-3 py-2 font-mono text-xs">{d.event}</td>
                                                <td className="px-3 py-2">
                                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                                                        d.status === "delivered" ? "bg-emerald-50 text-emerald-700"
                                                            : d.status === "failed" ? "bg-amber-50 text-amber-800"
                                                            : "bg-red-50 text-red-700"}`}>{d.status}</span>
                                                </td>
                                                <td className="px-3 py-2 font-mono text-xs">{d.response_status || "—"}</td>
                                                <td className="px-3 py-2 font-mono text-xs">{d.attempt}</td>
                                                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[220px]">{d.response_body}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Surface>
                    )}
                </TabsContent>

                {/* ---------------- docs ---------------- */}
                <TabsContent value="docs">
                    <Surface className="p-5 mt-3 space-y-4">
                        <div>
                            <h3 className="font-semibold flex items-center gap-2"><Code2 className="size-4" /> Making a request</h3>
                            <p className="text-sm text-muted-foreground mt-1">Send your key in the <code className="px-1 rounded bg-muted text-xs">X-API-Key</code> header. Every response carries your remaining rate allowance.</p>
                            <pre className="bg-muted rounded-md p-3 mt-2 text-xs overflow-x-auto">{`curl ${origin}/api/v1/employees?limit=10 \\
  -H "X-API-Key: sahod_sk_..."`}</pre>
                        </div>
                        <div>
                            <h3 className="font-semibold">What a key can reach</h3>
                            <div className="mt-2 space-y-1">
                                {scopes.map((s) => (
                                    <div key={s.scope} className="flex gap-3 text-sm">
                                        <code className="text-xs bg-secondary px-1.5 py-0.5 rounded shrink-0 w-44">{s.scope}</code>
                                        <span className="text-muted-foreground">{s.description}</span>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Salary, bank details and government IDs are never returned by <code className="px-1 rounded bg-muted">/v1/employees</code> —
                                compensation data only comes through the payroll endpoints, which need <code className="px-1 rounded bg-muted">payroll.read</code>.
                            </p>
                        </div>
                        <div>
                            <h3 className="font-semibold">Verifying a webhook</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Each delivery is signed with your endpoint's secret. Recompute it and compare — reject anything that doesn't match or is older than five minutes.
                            </p>
                            <pre className="bg-muted rounded-md p-3 mt-2 text-xs overflow-x-auto">{`# Python
import hmac, hashlib
signed = f"{headers['X-SAHOD-Timestamp']}.{raw_body}"
expected = hmac.new(SECRET.encode(), signed.encode(), hashlib.sha256).hexdigest()
valid = hmac.compare_digest(expected, headers['X-SAHOD-Signature'].removeprefix('sha256='))`}</pre>
                        </div>
                        <div>
                            <h3 className="font-semibold">Events you can subscribe to</h3>
                            <div className="mt-2 space-y-1">
                                {events.map((e) => (
                                    <div key={e.event} className="flex gap-3 text-sm">
                                        <code className="text-xs bg-secondary px-1.5 py-0.5 rounded shrink-0 w-44">{e.event}</code>
                                        <span className="text-muted-foreground">{e.description}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Surface>
                </TabsContent>
            </Tabs>

            {/* new key dialog */}
            <Dialog open={openKey} onOpenChange={setOpenKey}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>New API key</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Name</Label><Input value={keyForm.name} placeholder="BI dashboard"
                                                       onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })} data-testid="api-key-name" /></div>
                        <div>
                            <Label>Scopes</Label>
                            <p className="text-xs text-muted-foreground mb-1.5">Grant only what the integration needs.</p>
                            <div className="space-y-1 max-h-56 overflow-auto border border-border rounded-md p-2">
                                {scopes.map((s) => (
                                    <label key={s.scope} className="flex items-start gap-2 text-sm py-0.5">
                                        <input type="checkbox" className="mt-1" checked={keyForm.scopes.includes(s.scope)}
                                               onChange={() => toggle(keyForm.scopes, s.scope, (v) => setKeyForm({ ...keyForm, scopes: v }))} />
                                        <span>
                                            <code className="text-xs">{s.scope}</code>
                                            <span className="block text-xs text-muted-foreground">{s.description}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Rate limit (per hour)</Label><Input type="number" value={keyForm.rate_limit_per_hour}
                                                                           onChange={(e) => setKeyForm({ ...keyForm, rate_limit_per_hour: e.target.value })} /></div>
                            <div><Label>Expires in (days, optional)</Label><Input type="number" value={keyForm.expires_in_days} placeholder="never"
                                                                                 onChange={(e) => setKeyForm({ ...keyForm, expires_in_days: e.target.value })} /></div>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createKey} data-testid="api-key-save">Create key</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* key shown once */}
            <Dialog open={!!newKey} onOpenChange={(v) => !v && setNewKey(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Key for {newKey?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
                            Copy this now — it is never shown again. If it's lost, delete the key and issue another.
                        </div>
                        <div className="flex items-center gap-2">
                            <Input readOnly value={newKey?.api_key || ""} className="font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="api-key-value" />
                            <button className="h-9 px-3 rounded-md bg-secondary text-sm shrink-0" onClick={() => copy(newKey.api_key)}><Copy className="size-4" /></button>
                        </div>
                        <pre className="bg-muted rounded-md p-3 text-[11px] overflow-x-auto">{`curl ${origin}/api/v1/ping \\
  -H "X-API-Key: ${newKey?.api_key || ""}"`}</pre>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => setNewKey(null)}>Done</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* new webhook dialog */}
            <Dialog open={openHook} onOpenChange={setOpenHook}>
                <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>New webhook endpoint</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>URL</Label><Input value={hookForm.url} placeholder="https://example.com/hooks/sahod"
                                                      className="font-mono text-xs"
                                                      onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })} data-testid="api-hook-url" /></div>
                        <div><Label>Description (optional)</Label><Input value={hookForm.description}
                                                                        onChange={(e) => setHookForm({ ...hookForm, description: e.target.value })} /></div>
                        <div>
                            <Label>Events</Label>
                            <div className="space-y-1 max-h-56 overflow-auto border border-border rounded-md p-2 mt-1">
                                {events.map((e) => (
                                    <label key={e.event} className="flex items-start gap-2 text-sm py-0.5">
                                        <input type="checkbox" className="mt-1" checked={hookForm.events.includes(e.event)}
                                               onChange={() => toggle(hookForm.events, e.event, (v) => setHookForm({ ...hookForm, events: v }))} />
                                        <span>
                                            <code className="text-xs">{e.event}</code>
                                            <span className="block text-xs text-muted-foreground">{e.description}</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={createHook} data-testid="api-hook-save">Create endpoint</button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* webhook secret shown once */}
            <Dialog open={!!newHook} onOpenChange={(v) => !v && setNewHook(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Signing secret</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900">
                            Give this to whoever builds the receiver — they need it to verify that deliveries really came from SAHOD.
                        </div>
                        <div className="flex items-center gap-2">
                            <Input readOnly value={newHook?.secret || ""} className="font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="api-hook-secret" />
                            <button className="h-9 px-3 rounded-md bg-secondary text-sm shrink-0" onClick={() => copy(newHook.secret)}><Copy className="size-4" /></button>
                        </div>
                    </div>
                    <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={() => setNewHook(null)}>Done</button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
